import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import {
  RiBriefcaseLine,
  RiCheckLine,
  RiCodeSSlashLine,
  RiCloseCircleLine,
  RiSettings3Line,
} from "react-icons/ri";
import { Button, Form, FormGroup, Input, Label, Spinner } from "reactstrap";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import BottyAI from "src/modules/data-protection/assets/images/ai-botty/bottyAi.png";
import { SOFTWARE_TYPES } from "../helpers/constants";
import { mapAnalyzePrefill } from "../helpers/prefill";
import {
  getSoftwareUrl,
  isOwnDevelopmentRoute,
  isStandardRoute,
  requiresSoftwareContext,
  shouldAnalyzeSoftware,
} from "../helpers/routing";
import { isValidUrl } from "../helpers/url";
import WizardProgress from "./WizardProgress";

const SOFTWARE_TYPE_ICONS = {
  standard: RiBriefcaseLine,
  technical: RiSettings3Line,
  "own-development": RiCodeSSlashLine,
};

const ChoiceCard = ({ checked, description, icon: Icon, label, onClick }) => (
  <button
    className={`software-choice-card ${checked ? "is-selected" : ""}`}
    type="button"
    onClick={onClick}
  >
    {Icon && (
      <span className="software-choice-card__icon">
        <Icon />
      </span>
    )}
    <span className="software-choice-card__content">
      <strong>{label}</strong>
      {description && <small>{description}</small>}
    </span>
    <span className="software-choice-card__radio">
      {checked && <span className="software-choice-card__radio-dot" />}
    </span>
  </button>
);

const SoftwareDetailsStep = ({
  initialValues,
  onAnalyzed,
  onChangeStep,
  t,
  toggleModal,
}) => {
  const [screen, setScreen] = useState(
    initialValues?.softwareName
      ? requiresSoftwareContext(initialValues)
        ? "context"
        : "type"
      : "name"
  );
  const [softwareName, setSoftwareName] = useState(
    initialValues?.softwareName || ""
  );
  const [softwareType, setSoftwareType] = useState(
    initialValues?.softwareType || ""
  );
  const [providerUrl, setProviderUrl] = useState(
    initialValues?.providerUrl || ""
  );
  const [usage, setUsage] = useState(initialValues?.usage || "");
  const [marketAvailable, setMarketAvailable] = useState(
    initialValues?.marketAvailable || ""
  );
  const [publicUrl, setPublicUrl] = useState(initialValues?.publicUrl || "");
  const [errors, setErrors] = useState({});
  const [nameAvailability, setNameAvailability] = useState("idle");

  const checkNameMutation = useMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return service.checkSoftwareExistence(payload);
    },
    onSuccess: ({ exists }) => {
      setNameAvailability(exists ? "unavailable" : "available");
    },
    onError: () => setNameAvailability("error"),
  });
  const { mutate: checkSoftwareName } = checkNameMutation;

  const analyzeMutation = useMutation({
    mutationFn: async (payload) => {
      onChangeStep("loading");
      const service = DataProtectionSoftwareService.getInstance();
      return service.analyzeUrl(payload);
    },
    onSuccess: (prefill) => {
      onAnalyzed(
        mapAnalyzePrefill(prefill, {
          softwareName,
          softwareType,
          providerUrl,
          usage,
          marketAvailable,
          publicUrl,
          url: getSoftwareUrl({
            marketAvailable,
            providerUrl,
            publicUrl,
            softwareType,
            usage,
          }),
          isOwnDevelopment: isOwnDevelopmentRoute({ softwareType }),
        })
      );
      onChangeStep("details");
    },
    onError: () => onChangeStep("failed"),
  });

  const checkName = useMemo(
    () =>
      debounce((name) => {
        if (!name.trim()) {
          setNameAvailability("idle");
          return;
        }
        setNameAvailability("checking");
        checkSoftwareName({ service: name.trim() });
      }, 900),
    [checkSoftwareName]
  );

  useEffect(() => () => checkName.cancel(), [checkName]);

  const getWizardState = () => {
    const context = {
      softwareName,
      softwareType,
      providerUrl,
      usage,
      marketAvailable,
      publicUrl,
    };

    return {
      ...context,
      url: getSoftwareUrl(context),
      isOwnDevelopment: isOwnDevelopmentRoute(context),
    };
  };

  const continueWithWizard = (wizard) => {
    onAnalyzed(mapAnalyzePrefill({}, wizard));

    if (!shouldAnalyzeSoftware(wizard)) {
      onChangeStep("details");
      return;
    }

    analyzeMutation.mutate({
      service: softwareName,
      url: wizard.url,
      is_own_development: wizard.isOwnDevelopment,
    });
  };

  const goFromName = () => {
    if (!softwareName.trim()) {
      setErrors({ softwareName: t("Software name is required.") });
      return;
    }
    if (nameAvailability === "unavailable") {
      return;
    }
    setErrors({});
    setScreen("type");
  };

  const goFromType = () => {
    const nextErrors = {};
    if (!softwareType) {
      nextErrors.softwareType = t("Please select a software type.");
    }
    if (isStandardRoute({ softwareType }) && !isValidUrl(providerUrl)) {
      nextErrors.providerUrl = t("Valid URL is required for provider.");
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    if (requiresSoftwareContext({ softwareType })) {
      setScreen("context");
      return;
    }

    continueWithWizard(getWizardState());
  };

  const completeContext = () => {
    const nextErrors = {};
    if (!usage) {
      nextErrors.usage = t("Please select how this software is used.");
    }
    if (usage === "external" && !marketAvailable) {
      nextErrors.marketAvailable = t(
        "Please select whether this software is available on the market."
      );
    }
    if (
      usage === "external" &&
      marketAvailable === "yes" &&
      !isValidUrl(publicUrl)
    ) {
      nextErrors.publicUrl = t("A valid public URL is required.");
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    continueWithWizard(getWizardState());
  };

  const renderNameScreen = () => (
    <>
      <img className="software-wizard-bot software-wizard-bot--large" src={BottyAI} alt="Botty" />
      <div className="software-wizard-form">
        <h3>{t("Adding new asset? Let me help you with that!")}</h3>
        <FormGroup>
          <Label for="software-wizard-name">{t("Name of Software")}</Label>
          <Input
            id="software-wizard-name"
            value={softwareName}
            invalid={!!errors.softwareName}
            onChange={(event) => {
              const value = event.target.value;
              setSoftwareName(value);
              setErrors({});
              checkName(value);
            }}
          />
          {nameAvailability === "checking" && (
            <small className="text-info">
              <Spinner size="sm" /> {t("Checking software name")}...
            </small>
          )}
          {nameAvailability === "available" && (
            <small className="text-success">
              <RiCheckLine /> {t("This software name is available.")}
            </small>
          )}
          {nameAvailability === "unavailable" && (
            <small className="text-danger">
              <RiCloseCircleLine /> {t("This software name is already in use.")}
            </small>
          )}
          {nameAvailability === "error" && (
            <small className="text-danger">
              {t("Unable to verify the software name. Please try again.")}
            </small>
          )}
          {errors.softwareName && (
            <small className="text-danger">{errors.softwareName}</small>
          )}
        </FormGroup>
        <div className="software-wizard-actions">
          <Button outline color="primary" type="button" onClick={toggleModal}>
            {t("Cancel")}
          </Button>
          <Button
            color="primary"
            type="button"
            disabled={
              nameAvailability === "checking" ||
              nameAvailability === "unavailable"
            }
            onClick={goFromName}
          >
            {t("Next")}
          </Button>
        </div>
      </div>
    </>
  );

  const renderTypeScreen = () => (
    <>
      <img className="software-wizard-bot software-wizard-bot--small" src={BottyAI} alt="Botty" />
      <div className="software-wizard-form">
        <h2 className="text-center">{t("What best describes this software?")}</h2>
        <div className="software-choice-list">
          {SOFTWARE_TYPES.map(({ value, label, description }) => {
            const Icon = SOFTWARE_TYPE_ICONS[value];
            return (
            <ChoiceCard
              key={value}
              checked={softwareType === value}
              description={t(description)}
              icon={Icon}
              label={t(label)}
              onClick={() => {
                setSoftwareType(value);
                if (value === "own-development") {
                  setProviderUrl("");
                }
                setErrors({});
              }}
            />
            );
          })}
        </div>
        {errors.softwareType && (
          <small className="text-danger">{errors.softwareType}</small>
        )}
        {softwareType === "standard" && (
          <FormGroup className="mt-4">
            <Label for="software-wizard-provider-url">
              {t("Page of The Software Provider (URL)")}
            </Label>
            <Input
              id="software-wizard-provider-url"
              value={providerUrl}
              invalid={!!errors.providerUrl}
              onChange={(event) => {
                setProviderUrl(event.target.value);
                setErrors((current) => ({ ...current, providerUrl: "" }));
              }}
            />
            {errors.providerUrl && (
              <small className="text-danger">{errors.providerUrl}</small>
            )}
          </FormGroup>
        )}
        <div className="software-wizard-actions">
          <Button outline color="primary" type="button" onClick={() => setScreen("name")}>
            {t("Back")}
          </Button>
          <Button color="primary" type="button" onClick={goFromType}>
            {t("Next")}
          </Button>
        </div>
      </div>
    </>
  );

  const renderContextScreen = () => (
    <>
      <img className="software-wizard-bot software-wizard-bot--context" src={BottyAI} alt="Botty" />
      <div className="software-wizard-form">
        <h2 className="text-center">{t("How is this software used?")}</h2>
        <div className="software-choice-grid">
          <ChoiceCard
            checked={usage === "internal"}
            label={t("Internal use")}
            description={t(
              "Used only within your organization by employees or internal teams."
            )}
            onClick={() => {
              setUsage("internal");
              setMarketAvailable("");
              setPublicUrl("");
              setErrors({});
            }}
          />
          <ChoiceCard
            checked={usage === "external"}
            label={t("External use")}
            description={t(
              "Used by customers, clients, partners, or other organizations outside your company."
            )}
            onClick={() => {
              setUsage("external");
              setErrors({});
            }}
          />
        </div>
        {errors.usage && <small className="text-danger">{errors.usage}</small>}

        {usage === "external" && (
          <div className="software-market-section">
            <h2 className="text-center">
              {t("Is this software available on the market?")}
            </h2>
            <div className="software-choice-grid">
              <ChoiceCard
                checked={marketAvailable === "yes"}
                label={t("Yes — it’s available on the market")}
                onClick={() => {
                  setMarketAvailable("yes");
                  setErrors({});
                }}
              />
              <ChoiceCard
                checked={marketAvailable === "no"}
                label={t("No — it’s not available on the market")}
                onClick={() => {
                  setMarketAvailable("no");
                  setPublicUrl("");
                  setErrors({});
                }}
              />
            </div>
            {errors.marketAvailable && (
              <small className="text-danger">{errors.marketAvailable}</small>
            )}
            {marketAvailable === "yes" && (
              <FormGroup className="mt-3">
                <Label for="software-wizard-public-url">{t("Paste Public URL")}</Label>
                <Input
                  id="software-wizard-public-url"
                  value={publicUrl}
                  invalid={!!errors.publicUrl}
                  onChange={(event) => {
                    setPublicUrl(event.target.value);
                    setErrors((current) => ({ ...current, publicUrl: "" }));
                  }}
                />
                {errors.publicUrl && (
                  <small className="text-danger">{errors.publicUrl}</small>
                )}
              </FormGroup>
            )}
          </div>
        )}

        <div className="software-wizard-actions">
          <Button outline color="primary" type="button" onClick={() => setScreen("type")}>
            {t("Back")}
          </Button>
          <Button color="primary" type="button" onClick={completeContext}>
            {t("Next")}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <Form className="software-wizard" onSubmit={(event) => event.preventDefault()}>
      <WizardProgress activeStep={screen === "context" ? 2 : 1} t={t} />
      {screen === "name" && renderNameScreen()}
      {screen === "type" && renderTypeScreen()}
      {screen === "context" && renderContextScreen()}
    </Form>
  );
};

export default SoftwareDetailsStep;
